import { ADMIN_ORG_ID } from '@/apis/users-apis';
import { toaster } from '@/components/ui/toaster-instance';
import { useAuthInfo } from '@propelauth/react';
import { createContext, useCallback, useMemo } from 'react';

// eslint-disable-next-line react-refresh/only-export-components -- shared permission constants
export enum Permission {
  CAN_UNDO_NEXUS_DISREGARD = 'can_undo_nexus_disregard',
  CAN_EDIT_FILED_AMOUNTS = 'can_edit_filed_amounts',
  CAN_EDIT_ORG_SETTINGS = 'can_edit_org_settings',
  // Packaging Access Settings and country coverage. Billing details stay on
  // CAN_EDIT_ORG_SETTINGS.
  CAN_EDIT_FEATURE_CONTROLS = 'can_edit_feature_controls',
  CAN_EDIT_DO_NOT_FILE = 'can_edit_do_not_file',
  CAN_DELETE_ITEMS = 'can_delete_items',
  CAN_MANAGE_KINTSUGI_REGISTRATIONS = 'can_manage_kintsugi_registrations',
  CAN_MANAGE_KINTSUGI_FILINGS = 'can_manage_kintsugi_filings',
  CAN_MANAGE_PARTNERS = 'can_manage_partners',
  CAN_MANAGE_VERTEX_PARTITIONS = 'can_manage_vertex_partitions',
  CAN_ACCESS_FILING_TOOLS = 'can_access_filing_tools',
  CAN_RUN_SENSITIVE_ORG_TOOLS = 'can_run_sensitive_org_tools',
  // Engineering-only gate for Dev Tools (DLQ inspect / redrive / clear).
  // Must stay aligned with backend AdminPermission.
  CAN_USE_DEV_TOOLS = 'can_use_dev_tools',
  // Least-privilege gate for the health-check wizard's customer-outreach emails
  // (reconnect / re-engagement / QBR). Mirrors backend AdminPermission; kept
  // separate from CAN_RUN_SENSITIVE_ORG_TOOLS so reviewers can nudge customers
  // without also holding the destructive org tools.
  CAN_SEND_HEALTH_CHECK_OUTREACH = 'can_send_health_check_outreach',
  CAN_UPDATE_TAX_RULES = 'can_update_tax_rules',
  // Only picks the CX owner list in the org-page CSM Owner dropdown. No route checks it.
  // Mirrors backend AdminPermission.
  CAN_VIEW_CSM_WORKSPACE = 'can_view_csm_workspace',
  // Platform-versions endpoint. Mirrors backend AdminPermission;
  // kept separate from CAN_RUN_SENSITIVE_ORG_TOOLS.
  CAN_VIEW_PLATFORM_VERSIONS = 'can_view_platform_versions',
  // Bulk outreach tool, held by a small named group. Mirrors backend
  // AdminPermission; the backend enforces it, this only hides controls.
  CAN_SEND_BULK_OUTREACH = 'can_send_bulk_outreach',
  // Edits the Tax Ops team and its hours. Mirrors backend AdminPermission;
  // the backend enforces it, this only hides controls.
  CAN_MANAGE_TAX_OPS_TEAM = 'can_manage_tax_ops_team',
  // Turns an org's special handling on, edits it and turns it off. Mirrors
  // backend AdminPermission; the backend enforces it, this only hides the tool.
  CAN_MANAGE_SPECIAL_HANDLING = 'can_manage_special_handling',
}

// eslint-disable-next-line react-refresh/only-export-components -- shared auth helper
export const showPermissionDeniedToast = (permissionId: string): void => {
  toaster.create({
    title: 'Permission denied',
    description: `You do not have this permission: ${permissionId}`,
    type: 'error',
  });
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  // RequiredAuthProvider only mounts children after auth has loaded, so accessHelper is present here.
  const { accessHelper } = useAuthInfo();

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!accessHelper) return false;
      try {
        return accessHelper.hasPermission(ADMIN_ORG_ID, permission);
      } catch (error) {
        console.error('Error checking permission:', error);
        return false;
      }
    },
    [accessHelper]
  );

  const ensurePermission = useCallback(
    (permission: Permission): boolean => {
      if (hasPermission(permission)) return true;
      showPermissionDeniedToast(permission);
      return false;
    },
    [hasPermission]
  );

  const canManageVertexPartitions = useCallback((): boolean => {
    if (!accessHelper) return false;
    try {
      return accessHelper.hasPermission(ADMIN_ORG_ID, Permission.CAN_MANAGE_VERTEX_PARTITIONS);
    } catch (error) {
      console.error('Error checking Vertex partition permission:', error);
      return false;
    }
  }, [accessHelper]);

  return {
    ensurePermission,
    hasPermission,
    canManageVertexPartitions,
  };
};

type AuthContextType = {
  ensurePermission: (permission: Permission) => boolean;
  hasPermission: (permission: Permission) => boolean;
  canManageVertexPartitions: () => boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { ensurePermission, hasPermission, canManageVertexPartitions } = useAuth();
  const value = useMemo(
    () => ({ ensurePermission, hasPermission, canManageVertexPartitions }),
    [ensurePermission, hasPermission, canManageVertexPartitions]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
